import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, KeyRound, Loader2, MapPin, Navigation, Pencil, Play, Plus, Square } from "lucide-react";
import { api, del, errorMessage, patch } from "@/lib/api";
import { ADDRESS_STATUSES, STATUS_STYLES, FUTURE_STATUS_HINT, isFutureRun, statusAllowed } from "@/lib/constants";
import { StatusBadge } from "./StatusBadge";
import { Attachments } from "./Attachments";
import { ShutterNote } from "./ShutterFields";
import { LockChangedNote } from "./LockChangedFields";
import { LockChangeDialog } from "./LockChangeDialog";
import { WalkAwayDialog, WalkAwayNote } from "./WalkAwayDialog";
import { CancelDialog, CancelNote } from "./CancelDialog";
import { AddressTimes } from "./AddressTimes";
import { MissingTimesDialog } from "./MissingTimesDialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TEAM_ROLE_LABELS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { cleanMeterDetails, hasIncompleteMeterDetails, MeterDetailsFields, normalizeMeterDetails } from "@/components/MeterDetailsFields";

export const AddressCard = ({ address, index, onSaved, onFilesChanged, onEdit, isManager = false, runDate, warrantTypes }) => {
  const { profile } = useAuth();
  const [roleNotes, setRoleNotes] = useState({ notes: address.notes || "", agent_notes: address.agent_notes || "", engineer_notes: address.engineer_notes || "", dog_handler_notes: address.dog_handler_notes || "" });
  const [busy, setBusy] = useState(null);
  const [lockOpen, setLockOpen] = useState(false);
  const [walkOpen, setWalkOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [pendingTimes, setPendingTimes] = useState(null);
  const [meterDialogOpen, setMeterDialogOpen] = useState(false);
  const [meterDraft, setMeterDraft] = useState([]);
  const [meterError, setMeterError] = useState("");
  const meterPhotoInputs = useRef({});
  const tid = `address-card-${index}`;

  useEffect(() => {
    setRoleNotes({ notes: address.notes || "", agent_notes: address.agent_notes || "", engineer_notes: address.engineer_notes || "", dog_handler_notes: address.dog_handler_notes || "" });
  }, [address.notes, address.agent_notes, address.engineer_notes, address.dog_handler_notes]);

  const save = async (changes, kind) => {
    setBusy(kind);
    try {
      onSaved(await patch(`/addresses/${address.id}`, changes));
      toast.success(kind === "notes" ? "Notes saved" : kind === "lock" ? "Lock details saved" : kind === "meters" ? "Meter details saved" : kind === "start" ? "Job started" : kind === "end" ? "Job ended" : "Status updated");
      setWalkOpen(false);
      setCancelOpen(false);
      setPendingTimes(null);
      if (kind === "meters") setMeterDialogOpen(false);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const uploadMeterPhoto = async (meterIndex, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Choose an image file");
    if (file.size > 10 * 1024 * 1024) return toast.error("Photo must be 10 MB or smaller");
    let uploadedFileId;
    setBusy(`meter-photo-${meterIndex}`);
    try {
      const body = new FormData();
      body.append("file", file);
      const uploaded = await api.post(`/addresses/${address.id}/files`, body);
      uploadedFileId = uploaded.data.id;
      const meters = cleanMeterDetails(normalizeMeterDetails(address.meter_details).map((meter, meterIndexValue) => (
        meterIndexValue === meterIndex ? { ...meter, photo_file_id: uploadedFileId } : meter
      )));
      onSaved(await patch(`/addresses/${address.id}`, { meter_details: meters }));
      toast.success("Meter photo saved");
    } catch (error) {
      if (uploadedFileId) {
        await del(`/files/${uploadedFileId}`).catch(() => null);
      }
      toast.error(errorMessage(error));
    } finally {
      setBusy(null);
    }
  };
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${address.full_address} ${address.postcode}`)}`;
  const roles = warrantTypes || ["locksmith"];
  const canSeeAllRoles = isManager || profile?.role === "manager";
  const profileTypes = profile?.user_types?.length ? profile.user_types : [profile?.role];
  const visibleRoles = canSeeAllRoles ? roles : roles.filter((role) => profileTypes.includes(role));
  const roleTabs = ["agent", "engineer", "locksmith", "dog_handler"].filter((role) => visibleRoles.includes(role));
  const multipleTypes = roleTabs.length > 1;
  const timeRole = ["agent", "engineer", "dog_handler", "locksmith"].find((role) => profileTypes.includes(role) && roles.includes(role)) || "locksmith";
  const startedField = timeRole === "locksmith" ? "started_at" : `${timeRole}_started_at`;
  const endedField = timeRole === "locksmith" ? "ended_at" : `${timeRole}_ended_at`;
  const meterPhotoIds = new Set(normalizeMeterDetails(address.meter_details).map((meter) => meter.photo_file_id).filter(Boolean));
  const addressAttachments = { ...address, files: (address.files || []).filter((file) => !meterPhotoIds.has(file.id)) };
  const needsTimes = (status) => (status === "completed" || status === "walk_away") && !address[startedField] && !isFutureRun(runDate);
  const changeStatus = (changes) => {
    if (needsTimes(changes.status)) {
      setWalkOpen(false);
      setPendingTimes(changes);
    } else save(changes, "status");
  };
  const renderRoleDetails = (role, includeTimes = false) => (
    <div className="space-y-3" data-testid={`${tid}-${role}-details`}>
      {role === "agent" && (address.customer_name || address.customer_telephone || address.debt_amount || address.vulnerability_text || address.previous_visits != null) && (
        <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
          <p className="font-semibold text-slate-900">Agent details</p>
          {(address.customer_name || address.customer_telephone) && <p>{address.customer_name || "Customer"}{address.customer_telephone && <> · <a href={`tel:${address.customer_telephone}`} className="text-blue-700 hover:underline">{address.customer_telephone}</a></>}</p>}
          {address.debt_amount && <p><span className="font-medium">Debt amount:</span> {address.debt_amount}</p>}
          {address.previous_visits != null && <p><span className="font-medium">Previous visits:</span> {address.previous_visits}</p>}
          {address.vulnerability_text && <p className="whitespace-pre-wrap"><span className="font-medium">Vulnerability:</span> {address.vulnerability_text}</p>}
        </div>
      )}
      {role === "locksmith" && <>
        <ShutterNote address={address} testId={`${tid}-shutter`} />
        <LockChangedNote address={address} testId={`${tid}-lock`} />
        <div className="pt-[10px]">
          <Button size="sm" variant="outline" onClick={() => setLockOpen(true)} className="min-h-[40px]" data-testid={`${tid}-lock-change-button`}>
            <KeyRound className="mr-1 h-4 w-4" /> {address.lock_changed ? "Edit lock change" : "Lock change"}
          </Button>
        </div>
      </>}
      {role === "engineer" && address.engineer_type && (
        <p className="mt-[20px] rounded-lg border-l-4 border-cyan-600 bg-cyan-50 p-3 text-sm text-slate-800" data-testid={`${tid}-engineer-type`}><span className="font-semibold">Job Type:</span> {address.engineer_type}</p>
      )}
      {role === "engineer" && (
        <div className="rounded-lg border-l-4 border-emerald-600 bg-emerald-50 p-3 text-sm text-slate-800" data-testid={`${tid}-meter-details`}>
          <div className="flex items-center justify-between gap-3">
            <p className="font-semibold">Meter Details</p>
            <Button type="button" size="sm" variant="outline" disabled={!!busy || normalizeMeterDetails(address.meter_details).length >= 20} onClick={() => {
              setMeterDraft([...normalizeMeterDetails(address.meter_details), { type: "", serial: "", mpan: "", mprn: "", read: "" }]);
              setMeterError("");
              setMeterDialogOpen(true);
            }} data-testid={`${tid}-add-meter-button`}><Plus className="mr-1 h-4 w-4" />Add Meter</Button>
          </div>
          {normalizeMeterDetails(address.meter_details).length > 0 && (
            <ul className="mt-1 space-y-3">{normalizeMeterDetails(address.meter_details).map((meter, meterIndex) => (
              <li key={meterIndex}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>{meter.type || "Type not set"} · Serial: {meter.serial}{meter.mpan && ` · MPAN: ${meter.mpan}`}{meter.mprn && ` · MPRN: ${meter.mprn}`}{meter.read && ` · Read: ${meter.read}`}</span>
                  <input ref={(element) => { meterPhotoInputs.current[meterIndex] = element; }} type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif" hidden onChange={(event) => uploadMeterPhoto(meterIndex, event)} data-testid={`${tid}-meter-photo-input-${meterIndex}`} />
                  <Button type="button" size="sm" variant="outline" disabled={!!busy} onClick={() => meterPhotoInputs.current[meterIndex]?.click()} data-testid={`${tid}-meter-photo-button-${meterIndex}`}>
                    {busy === `meter-photo-${meterIndex}` ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Camera className="mr-1 h-4 w-4" />}{meter.photo_file_id ? "Change photo" : "Add photo"}
                  </Button>
                </div>
                {meter.photo_file_id && <Attachments address={{ ...address, files: (address.files || []).filter((file) => file.id === meter.photo_file_id) }} testId={`${tid}-meter-${meterIndex}`} onChanged={onFilesChanged} canUpload={false} canDelete={false} title={`Meter ${meterIndex + 1} photo`} />}
              </li>
            ))}</ul>
          )}
        </div>
      )}
      {(() => {
        const field = role === "locksmith" ? "notes" : `${role}_notes`;
        const notesId = role === "locksmith" ? `notes-${address.id}` : `notes-${address.id}-${role}`;
        const notesTid = role === "locksmith" ? tid : `${tid}-${role}`;
        const note = roleNotes[field] || "";
        return (
          <div className="pt-[10px]">
            <label htmlFor={notesId} className="flex items-center gap-1 text-sm font-medium text-slate-700"><MapPin className="h-3.5 w-3.5" />Notes</label>
            <Textarea id={notesId} rows={2} value={note} onChange={(e) => setRoleNotes((current) => ({ ...current, [field]: e.target.value }))} maxLength={2000} className="mt-1.5" placeholder={`Add ${role === "locksmith" ? "notes" : `${TEAM_ROLE_LABELS[role].toLowerCase()} notes`} for this address`} data-testid={`${notesTid}-notes-input`} />
            {note !== (address[field] || "") && (
              <div className="mt-2 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setRoleNotes((current) => ({ ...current, [field]: address[field] || "" }))} disabled={!!busy} data-testid={`${notesTid}-notes-reset`}>Discard</Button>
                <Button size="sm" className="bg-blue-700 hover:bg-blue-800" onClick={() => save({ [field]: note.trim() || null }, "notes")} disabled={!!busy} data-testid={`${notesTid}-notes-save`}>
                  {busy === "notes" && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}Save notes
                </Button>
              </div>
            )}
          </div>
        );
      })()}
      {includeTimes && <AddressTimes address={address} runDate={runDate} onSaved={onSaved} tid={tid} role={role} />}
    </div>
  );

  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={tid}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 gap-3">
          <span className="font-data flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-sm font-semibold text-white">{index + 1}</span>
          <div className="min-w-0">
            <p className="text-base font-semibold text-slate-900" data-testid={`${tid}-address`}>{address.full_address || <span className="font-normal italic text-slate-500">No address line (postcode only)</span>}</p>
            <p className="font-data text-sm font-bold text-blue-800" data-testid={`${tid}-postcode`}>{address.postcode}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <StatusBadge status={address.status} testId={`${tid}-status`} />
          {onEdit && (
            <Button size="icon" variant="ghost" onClick={() => onEdit(address)} aria-label="Edit address" className="h-9 w-9" data-testid={`${tid}-edit-button`}>
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      {typeof address.instructions === "string" && address.instructions.trim() && (
        <p className="mt-3 rounded-lg border-l-4 border-blue-600 bg-blue-50 p-3 text-sm text-slate-800"><span className="font-semibold">Manager notes: </span>{address.instructions}</p>
      )}
      {address.status === "scheduled" && !address[startedField] && !isFutureRun(runDate) && (
        <Button onClick={() => save({ status: "in_progress" }, "start")} disabled={!!busy} className="mt-4 min-h-[52px] w-full bg-blue-700 text-base font-semibold hover:bg-blue-800" data-testid={`${tid}-start-job-button`}>
          {busy === "start" ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Play className="mr-2 h-5 w-5" />} Start Job
        </Button>
      )}
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" role="group" aria-label="Set status">
        {ADDRESS_STATUSES.map((s) => {
          const active = address.status === s.value;
          const locked = !statusAllowed(runDate, s.value);
          return (
            <button key={s.value} type="button" disabled={!!busy || active || locked} onClick={() => (s.value === "walk_away" ? setWalkOpen(true) : s.value === "cancelled" ? setCancelOpen(true) : changeStatus({ status: s.value }))} aria-pressed={active}
              title={locked ? FUTURE_STATUS_HINT : undefined}
              data-testid={`${tid}-status-${s.value}`}
              className={`min-h-[44px] rounded-lg border px-2 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${active ? `${STATUS_STYLES[s.value]} ring-2 ring-offset-1 ring-slate-300` : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"} ${locked && !active ? "cursor-not-allowed opacity-40 hover:bg-white" : ""} disabled:cursor-default`}>
              {busy === "status" && !active ? <span className="opacity-60">{s.label}</span> : s.label}
            </button>
          );
        })}
      </div>
      {isFutureRun(runDate) && <p className="mt-2 text-xs text-slate-500" data-testid={`${tid}-future-hint`}>{FUTURE_STATUS_HINT}</p>}
      <WalkAwayNote address={address} testId={`${tid}-walk-away-note`} onEdit={() => setWalkOpen(true)} />
      <CancelNote address={address} testId={`${tid}-cancel-note`} onEdit={() => setCancelOpen(true)} />
      {address.status === "customer_home" && !address[endedField] && (
        <Button onClick={() => save({ [endedField]: new Date().toISOString() }, "end")} disabled={!!busy} className="mt-4 min-h-[52px] w-full bg-indigo-700 text-base font-semibold hover:bg-indigo-800" data-testid={`${tid}-end-job-button`}>
          {busy === "end" ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Square className="mr-2 h-5 w-5" />} End Job
        </Button>
      )}
      {!multipleTypes && roleTabs.map((role) => renderRoleDetails(role))}
      {multipleTypes && (
        <Tabs defaultValue={roleTabs[0]} className="mt-4 w-full" data-testid={`${tid}-role-tabs`}>
          <TabsList className="grid h-auto w-full grid-cols-2 gap-1.5 rounded-xl border border-slate-200 bg-slate-100/80 p-1.5 sm:flex sm:flex-wrap sm:justify-start">
            {roleTabs.map((role) => <TabsTrigger key={role} value={role} className="min-h-10 gap-2 px-3 text-xs font-semibold text-slate-600 hover:text-slate-900 data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm sm:flex-1 sm:px-4 sm:text-sm" data-testid={`${tid}-role-tab-${role}`}>{TEAM_ROLE_LABELS[role]}</TabsTrigger>)}
          </TabsList>
          {roleTabs.map((role) => <TabsContent key={role} value={role} className="mt-4">{renderRoleDetails(role, true)}</TabsContent>)}
        </Tabs>
      )}
      {!multipleTypes && roleTabs.length === 1 && <AddressTimes address={address} runDate={runDate} onSaved={onSaved} tid={tid} role={roleTabs[0]} />}
      <a href={mapsUrl} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:underline" data-testid={`${tid}-maps-link`}><Navigation className="h-3.5 w-3.5" /> Open in Maps</a>
      <LockChangeDialog open={lockOpen} onOpenChange={setLockOpen} address={address} onSaved={onSaved} />
      <WalkAwayDialog open={walkOpen} onOpenChange={setWalkOpen} busy={busy === "status"} tid={tid} address={address} onConfirm={(extra) => (address.status === "walk_away" ? save(extra, "status") : changeStatus({ status: "walk_away", ...extra }))} />
      <MissingTimesDialog open={!!pendingTimes} onOpenChange={(o) => !o && setPendingTimes(null)} busy={busy === "status"} tid={tid} runDate={runDate} statusLabel={pendingTimes?.status === "walk_away" ? "Walk Away" : "Completed"}
        onConfirm={({ start, end }) => save({ ...pendingTimes, [startedField]: start, [endedField]: end }, "status")} />
      <CancelDialog open={cancelOpen} onOpenChange={setCancelOpen} busy={busy === "status"} tid={tid} address={address} onConfirm={(extra) => save(address.status === "cancelled" ? extra : { status: "cancelled", ...extra }, "status")} />
      <Dialog open={meterDialogOpen} onOpenChange={(open) => !busy && setMeterDialogOpen(open)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl" data-testid={`${tid}-add-meter-dialog`}>
          <DialogHeader><DialogTitle>Add Meter to address</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <MeterDetailsFields value={meterDraft} onChange={(meters) => { setMeterDraft(meters); setMeterError(""); }} testId={`${tid}-meter-editor`} />
            {meterError && <p role="alert" className="text-sm text-rose-700" data-testid={`${tid}-meter-error`}>{meterError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMeterDialogOpen(false)} disabled={!!busy}>Cancel</Button>
              <Button type="button" onClick={() => {
                if (hasIncompleteMeterDetails(meterDraft)) {
                  setMeterError("Each meter needs a type and serial");
                  return;
                }
                const meters = cleanMeterDetails(meterDraft);
                if (!meters.length) {
                  setMeterError("Enter a meter type and serial");
                  return;
                }
                if (meters.length > 20) {
                  setMeterError("A maximum of 20 meters can be added to one address");
                  return;
                }
                save({ meter_details: meters }, "meters");
              }} disabled={!!busy} data-testid={`${tid}-meter-save-button`}>{busy === "meters" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save meter details</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      <Attachments address={addressAttachments} testId={tid} onChanged={onFilesChanged} />
    </article>
  );
};
